import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit,
  inject,
  ChangeDetectorRef,
  ElementRef,
  ViewChild,
  Inject,
  PLATFORM_ID,
  NgZone,
} from '@angular/core';
import { ScrollToTop } from '../../components/common/scroll-to-top/scroll-to-top';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { Posts } from '../posts/posts';
import { Events } from '../events/events';
import { Blog } from '../blog/blog';
import { SearchService } from '../../shared/services/search.service';

const PAGE_SIZE = 12;

/**
 * "All" page — a single mixed feed of posts, blogs and events, newest first,
 * with just one search box (no All/Posts/Blogs/Events tabs).
 *
 * Data flow:
 *   1. Fetch a page from the global search endpoint (GET /api/v1/search) with
 *      no `type` filter — the backend already returns posts+blogs+events
 *      merged and sorted by createdAt desc.
 *   2. Tag every raw item with `_order` = its position in the combined feed
 *      so far, and append it to `allItems`.
 *   3. Split `allItems` by `type` into three arrays and hand each to
 *      <app-posts>/<app-blog>/<app-events> via their `[mixedItems]` input —
 *      those components skip their own fetch and just render the cards
 *      (with all of their normal reactions/follow/share/comment behaviour).
 *   4. Every card gets `[style.order]="item.globalOrder"` and each
 *      component's wrapper divs collapse via `display: contents`, so cards
 *      from all three components land as direct children of ONE shared CSS
 *      grid here and interleave strictly by createdAt, regardless of type.
 */
@Component({
  selector: 'app-all',
  standalone: true,
  imports: [CommonModule, ScrollToTop, FormsModule, Posts, Events, Blog],
  templateUrl: './all.html',
})
export class All implements OnInit, AfterViewInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private searchService = inject(SearchService);

  private paramsSub?: Subscription;
  private intersectionObserver?: IntersectionObserver;

  @ViewChild('scrollSentinel') sentinelRef!: ElementRef<HTMLElement>;

  /** Bound to the on-page search box (kept in sync with the ?q= query param). */
  searchInput = '';

  activeQuery?: string;
  activeHashtag?: string;

  /** People whose display name/username matched the current `q` — rendered
   *  as a "Profile" section above the normal mixed feed below. Only ever
   *  populated by a free-text search (never for a #hashtag search), and only
   *  refreshed on the first page of a query so it doesn't flicker away
   *  while the user infinite-scrolls through further pages. */
  profiles: any[] = [];

  /** Combined, globally-ordered feed (posts + blogs + events). */
  allItems: any[] = [];
  page = 1;
  total = 0;
  isLoading = false;
  allLoaded = false;

  /** Cards use CSS `overflow: visible` (masonry technique) so a card's real
   *  visual height can briefly exceed the grid's *measured* height — e.g. a
   *  video card whose lazy-mounted <video> hasn't loaded its dimensions yet.
   *  That means the grid's rendered CSS height can undercount, so the
   *  scroll sentinel can stay "intersecting" even though there isn't really
   *  more room on screen. Without a cooldown, that turns into a rapid-fire
   *  fetch loop (each new page adds more not-yet-measured cards, which keeps
   *  the sentinel stuck) that pegs the CPU and can hang the tab. This just
   *  gives layout/lazy-media a moment to catch up between fetches. */
  private static readonly FETCH_COOLDOWN_MS = 400;
  private cooldownActive = false;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private zone: NgZone,
  ) { }

  get hasActiveFilter(): boolean { return !!(this.activeQuery || this.activeHashtag); }

  // Stored (not computed-in-template) on purpose: these are bound directly
  // via [mixedItems] on <app-posts>/<app-blog>/<app-events>. A getter that
  // does `.filter()` returns a brand-new array reference on every single
  // Angular change-detection cycle — Angular re-evaluates getters on every
  // tick — so the child components would see "mixedItems changed" constantly
  // and re-run applyMixedItems() (which itself calls detectChanges()) even
  // when nothing actually changed. That's a self-sustaining change-detection
  // loop: any event on the page (a video firing loadedmetadata, a timer, an
  // IntersectionObserver callback) re-triggers CD, which re-evaluates the
  // getter, which looks like new data, which re-renders, forever — which is
  // exactly the constant destroy/recreate "blinking" on elements like the
  // <video> tag. Recomputing these only when allItems changes keeps the
  // references stable across unrelated change-detection runs.
  postItems: any[] = [];
  blogItems: any[] = [];
  eventItems: any[] = [];

  private recomputeGroupedItems(): void {
    this.postItems = this.allItems.filter(i => i.type === 'POST');
    this.blogItems = this.allItems.filter(i => i.type === 'BLOG');
    this.eventItems = this.allItems.filter(i => i.type === 'EVENT');
  }

  get isEmpty(): boolean {
    return !this.isLoading && this.allLoaded && this.allItems.length === 0 && this.profiles.length === 0;
  }

  ngOnInit(): void {
    this.paramsSub = this.route.queryParams.subscribe(params => {
      const q       = (params['q'] ?? '').trim();
      const hashtag = (params['hashtag'] ?? '').trim().replace(/^#+/, '');

      this.activeQuery   = q || undefined;
      this.activeHashtag = hashtag || undefined;
      this.searchInput   = hashtag ? `#${hashtag}` : q;

      this.resetAndReload();
    });
  }

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !this.isLoading && !this.allLoaded && !this.cooldownActive) {
          this.zone.run(() => this.loadNextPage());
        }
      },
      { threshold: 0 },
    );
    if (this.sentinelRef?.nativeElement) {
      this.intersectionObserver.observe(this.sentinelRef.nativeElement);
    }
  }

  private cooldownTimer?: ReturnType<typeof setTimeout>;

  private startCooldown(): void {
    this.cooldownActive = true;
    this.cooldownTimer = setTimeout(() => {
      this.cooldownActive = false;
      // Layout has now settled — if the sentinel is genuinely still visible
      // (e.g. a short page of results), give it one more real check.
      const el = this.sentinelRef?.nativeElement;
      if (el && !this.isLoading && !this.allLoaded) {
        const rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight && rect.bottom > 0) {
          this.loadNextPage();
        }
      }
    }, All.FETCH_COOLDOWN_MS);
  }

  ngOnDestroy(): void {
    this.paramsSub?.unsubscribe();
    this.intersectionObserver?.disconnect();
    if (this.cooldownTimer) clearTimeout(this.cooldownTimer);
  }

  private resetAndReload(): void {
    this.allItems = [];
    this.postItems = [];
    this.blogItems = [];
    this.eventItems = [];
    this.profiles = [];
    this.page = 1;
    this.total = 0;
    this.allLoaded = false;
    this.loadNextPage();
  }

  loadNextPage(): void {
    if (this.isLoading || this.allLoaded || this.cooldownActive) return;
    this.isLoading = true;
    this.cdr.detectChanges();

    const requestedPage = this.page;

    this.searchService.search(requestedPage, PAGE_SIZE, {
      q: this.activeQuery,
      hashtag: this.activeHashtag,
    }).subscribe({
      next: (response: any) => {
        const data = response?.data ?? {};
        const items: any[] = data.items ?? [];

        // The backend only computes matched profiles on page 1 of a
        // free-text query — on later infinite-scroll pages `data.profiles`
        // comes back empty, so only overwrite `profiles` on that first
        // fetch, otherwise the profile card would disappear as soon as the
        // user scrolls for more results.
        if (requestedPage === 1) {
          this.profiles = data.profiles ?? [];
        }

        // Tag each item with its position in the combined feed so the child
        // card components can set CSS `order` and interleave correctly.
        items.forEach((item) => {
          (item as any)._order = this.allItems.length;
          this.allItems = [...this.allItems, item];
        });

        this.total = data.total ?? this.allItems.length;
        this.page += 1;
        this.isLoading = false;
        this.allLoaded = this.allItems.length >= this.total || items.length === 0;
        this.recomputeGroupedItems();
        this.cdr.detectChanges();
        this.startCooldown();
      },
      error: (err) => {
        console.error('Error loading mixed feed:', err);
        this.isLoading = false;
        this.allLoaded = true;
        this.cdr.detectChanges();
      },
    });
  }

  submitSearch(): void {
    const raw = this.searchInput.trim();
    const isHashtag = raw.startsWith('#');
    const value = raw.replace(/^#+/, '');

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        q: !isHashtag && value ? value : null,
        hashtag: isHashtag && value ? value : null,
      },
      queryParamsHandling: 'merge',
    });
  }

  clearSearch(): void {
    this.searchInput = '';
    this.profiles = [];
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { q: null, hashtag: null },
      queryParamsHandling: 'merge',
    });
  }

  /** Navigates to a matched profile's page from the "Profile" section. */
  goToProfile(profile: any): void {
    if (!profile?.userName) return;
    this.router.navigate(['/profile', profile.userName, 'post']);
  }

  /** Placeholder avatar for a matched profile that has no profile image yet —
   *  same ui-avatars.com pattern used elsewhere across the app. */
  avatarFallback(name?: string | null): string {
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'U')}&background=FFD4E0&color=D31148`;
  }
}